/**
 * Query Template API Service
 *
 * Client for the data-service query template endpoints.
 * Enables chart components to fetch data using parameterized templates.
 */

import axios from 'axios';
import { config } from '@/config';
import { ChartQueryDefinition, CompositeQueryDefinition } from '@/types/chartDataSource';
import type { Organization } from '@/stores/organization/organization-store';
import {
  createDataServiceClient,
  getValidatedDataServiceOrganizationId,
} from './data-service-client';
import { getValidatedExplicitOrganization } from './client';

// ============================================================================
// Types
// ============================================================================

/**
 * Variable types for query templates
 */
export type QueryTemplateVariableType =
  | 'system'
  | 'timePreset'
  | 'dateRange'
  | 'enum'
  | 'string'
  | 'number';

/**
 * Variable definition
 */
export interface VariableDefinition {
  type: QueryTemplateVariableType;
  default?: string | number;
  options?: string[];
  required?: boolean;
  description?: string;
}

/**
 * Template measure
 */
export interface TemplateMeasure {
  field: string;
  aggregation: 'sum' | 'avg' | 'min' | 'max' | 'count' | 'last' | 'first';
  alias?: string;
}

/**
 * Template query structure
 */
export interface TemplateQuery {
  schemaId: string;
  groupId: string;
  timeRange?: {
    field?: string;
    from?: string;
    to?: string;
    preset?: string;
  };
  groupBy?: Array<string | { field: string; granularity: string }>;
  measures: TemplateMeasure[];
  filters?: Record<string, unknown>;
  orderBy?: Array<{ field: string; direction: 'asc' | 'desc' }>;
  limit?: number;
}

/**
 * Query template
 */
export interface QueryTemplate {
  id: string;
  _id?: string; // Deprecated: backend returns 'id'
  name: string;
  description?: string;
  templateType?: 'query-template' | 'composite-query';
  organizationId: string;
  createdBy: string;
  updatedBy: string;
  query?: TemplateQuery;
  compositeQueryDefinition?: CompositeQueryDefinition;
  variables: Record<string, VariableDefinition>;
  usageCount: number;
  lastUsedAt?: string;
  tags?: string[];
  folder?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Template execution result
 */
export interface TemplateExecutionResult {
  data: Record<string, unknown>[];
  metadata: {
    templateId: string;
    templateName: string;
    resolvedVariables: Record<string, unknown>;
    executionTimeMs: number;
    rowCount: number;
    cached: boolean;
    cacheKey?: string;
    skipCache?: boolean;
    userOrgId?: string;
    dataOrgId?: string;
    querySummary?: {
      schemaId?: string;
      groupId?: string;
      measures?: string[];
      filters?: string[];
      orderBy?: string[];
      groupBy?: string[];
      timeRange?: string;
      limit?: number;
    };
  };
}

/**
 * Create template DTO
 */
export interface CreateQueryTemplateDTO {
  name: string;
  description?: string;
  templateType?: 'query-template' | 'composite-query';
  query?: TemplateQuery;
  compositeQueryDefinition?: CompositeQueryDefinition;
  variables?: Record<string, VariableDefinition>;
  tags?: string[];
  folder?: string;
}

/**
 * List options
 */
export interface ListTemplatesOptions {
  tags?: string[];
  search?: string;
  schemaId?: string;
  groupId?: string;
  folder?: string;
  sortBy?: 'name' | 'createdAt' | 'usageCount';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  skip?: number;
}

/**
 * Options for cross-org data access in personal space.
 * When organizationId is set, the request sends an X-Organization-Id header
 * targeting that specific org instead of the global context.
 */
export interface CrossOrgOptions {
  organizationId?: string;
  skipCache?: boolean;
}

/**
 * API response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

interface VerifyAccessResult {
  hasAccess: boolean;
  dataOwnerOrgId?: string;
  dataMode?: string;
  accessLevel?: string;
}

const INVALID_CROSS_ORG_CONTEXT_MESSAGE =
  'Organization context unavailable for this request';
const COMPOSITE_TEMPLATE_SOURCE_OWNER_ERROR =
  'Composite query templates require explicit data owner organization IDs for every source';

/**
 * Paginated response
 */
interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const dataServiceClient = createDataServiceClient({
  baseURL: config.api.dataServiceUrl,
  timeout: config.api.timeout,
});

// ============================================================================
// API Functions
// ============================================================================

/**
 * Query Template API
 */
// ============================================================================
// Query Execution (via main backend data proxy)
// ============================================================================

export interface ExecuteQueryRequest {
  queryDefinition: ChartQueryDefinition;
}

export interface ExecuteQueryResponse {
  success: boolean;
  data: Record<string, unknown>[];
  metadata?: {
    rowCount: number;
    executionTimeMs: number;
    cached?: boolean;
  };
  error?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

function isRecordArray(value: unknown): value is Record<string, unknown>[] {
  return Array.isArray(value) && value.every(isRecord);
}

function getOptionalString(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  return typeof value === 'string' ? value : undefined;
}

function getOptionalBoolean(record: Record<string, unknown>, key: string): boolean | undefined {
  const value = record[key];
  return typeof value === 'boolean' ? value : undefined;
}

function isNonEmptyString(value: string | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function validateCompositeTemplateSourceOwners(dto: CreateQueryTemplateDTO): void {
  if (dto.templateType !== 'composite-query' && !dto.compositeQueryDefinition) {
    return;
  }

  const sources = dto.compositeQueryDefinition?.sources ?? [];
  if (
    sources.length === 0 ||
    sources.some((source) => !isNonEmptyString(source.dataOwnerOrganizationId))
  ) {
    throw new Error(COMPOSITE_TEMPLATE_SOURCE_OWNER_ERROR);
  }
}

function parseExecuteQueryMetadata(value: unknown): ExecuteQueryResponse['metadata'] {
  if (!isRecord(value)) return undefined;

  const rowCount = value.rowCount;
  const executionTimeMs = value.executionTimeMs;
  const cached = value.cached;

  if (typeof rowCount !== 'number' || typeof executionTimeMs !== 'number') {
    return undefined;
  }

  if (cached !== undefined && typeof cached !== 'boolean') {
    return undefined;
  }

  return cached === undefined
    ? { rowCount, executionTimeMs }
    : { rowCount, executionTimeMs, cached };
}

// Parse external aggregate response data at the API boundary instead of asserting
// a trusted shape onto `response.data`.
function parseAggregateExecuteResponse(payload: unknown): ExecuteQueryResponse {
  if (!isRecord(payload)) {
    return { success: true, data: [] };
  }

  const nestedData = isRecord(payload.data) ? payload.data : undefined;
  const results =
    nestedData?.results ??
    (Array.isArray(payload.data) ? payload.data : payload.results);

  return {
    success: getOptionalBoolean(payload, 'success') ?? true,
    data: isRecordArray(results) ? results : [],
    metadata: parseExecuteQueryMetadata(payload.metadata),
    error: getOptionalString(payload, 'error'),
  };
}

// Parse external template execution responses at the API boundary instead of
// asserting a trusted shape onto `response.data`.
function parseTemplateExecuteResponse(payload: unknown): ExecuteQueryResponse {
  if (!isRecord(payload)) {
    return { success: true, data: [] };
  }

  const nestedData = isRecord(payload.data) ? payload.data : undefined;
  const actualData = isRecordArray(nestedData?.data)
    ? nestedData.data
    : isRecordArray(payload.data)
      ? payload.data
      : [];

  return {
    success: getOptionalBoolean(payload, 'success') ?? true,
    data: actualData,
    metadata: parseExecuteQueryMetadata(nestedData?.metadata) ?? parseExecuteQueryMetadata(payload.metadata),
    error: getOptionalString(payload, 'error'),
  };
}

/**
 * Resolve the data-owning organization ID from available stores.
 * Priority: explicit override → viewer org → current org context → user's org
 */
async function resolveDataOwnerOrgId(overrideOrgId?: string): Promise<string> {
  if (overrideOrgId) {
    return (
      await getValidatedExplicitOrganization({ organizationId: overrideOrgId })
    )?._id ?? '';
  }

  return (await getValidatedDataServiceOrganizationId()) ?? '';
}

async function getValidatedCrossOrgOrganization(
  options?: Pick<CrossOrgOptions, 'organizationId'>,
): Promise<Organization | null> {
  if (!options?.organizationId) {
    return null;
  }

  return getValidatedExplicitOrganization({
    organizationId: options.organizationId,
  });
}

async function getValidatedCrossOrgAxiosConfig(
  options?: Pick<CrossOrgOptions, 'organizationId'>,
): Promise<import('axios').AxiosRequestConfig | undefined> {
  const organization = await getValidatedCrossOrgOrganization(options);
  if (options?.organizationId && !organization) {
    throw new Error(INVALID_CROSS_ORG_CONTEXT_MESSAGE);
  }

  return organization
    ? { headers: { 'X-Organization-Id': organization._id } }
    : undefined;
}

async function resolveDataOwnerOrgIdForQuery(
  queryDefinition: ChartQueryDefinition,
  options?: CrossOrgOptions
): Promise<string> {
  const explicitOrganization = await getValidatedCrossOrgOrganization(options);
  if (options?.organizationId && !explicitOrganization) {
    throw new Error(INVALID_CROSS_ORG_CONTEXT_MESSAGE);
  }

  if (explicitOrganization) {
    return explicitOrganization._id;
  }

  const resolvedFromContext = await resolveDataOwnerOrgId(options?.organizationId);
  if (resolvedFromContext) return resolvedFromContext;

  const axiosConfig = await getValidatedCrossOrgAxiosConfig(options);
  const accessResponse = await dataServiceClient.post<ApiResponse<VerifyAccessResult>>(
    '/api/nl/verify-access',
    {
      schemaId: queryDefinition.schemaId,
      groupId: queryDefinition.groupId,
    },
    axiosConfig,
  );
  const access = accessResponse.data.data;
  if (!accessResponse.data.success || !access?.hasAccess || !access.dataOwnerOrgId) {
    throw new Error(accessResponse.data.message || accessResponse.data.error || 'Unable to resolve data owner organization');
  }
  return access.dataOwnerOrgId;
}

/**
 * Execute a query definition directly (for chart data refresh).
 * Calls the data-service /api/nl/execute-template endpoint directly,
 * bypassing the main backend proxy.
 */
export async function executeQueryDefinition(
  queryDefinition: ChartQueryDefinition,
  options?: CrossOrgOptions
): Promise<ExecuteQueryResponse> {
  let axiosConfig: import('axios').AxiosRequestConfig | undefined;
  try {
    axiosConfig = await getValidatedCrossOrgAxiosConfig(options);
  } catch (error) {
    return {
      success: false,
      data: [],
      error: error instanceof Error ? error.message : INVALID_CROSS_ORG_CONTEXT_MESSAGE,
    };
  }

  // Diagnostic logging for debugging data fetch issues
  console.debug('[executeQueryDefinition] Executing', {
    schemaId: queryDefinition.schemaId,
    groupId: queryDefinition.groupId,
    measuresCount: queryDefinition.measures?.length,
    hasAiPipeline: !!queryDefinition.aiGeneratedPipeline?.length,
    dataOwnerOrgId: await resolveDataOwnerOrgId(options?.organizationId),
    organizationId: options?.organizationId,
  });

  // Guard: empty measures would fail analytics validation — return empty data instead
  if (!queryDefinition.aiGeneratedPipeline?.length && !queryDefinition.measures?.length) {
    console.warn('[executeQueryDefinition] No measures defined, returning empty data');
    return {
      success: true,
      data: [],
      metadata: { rowCount: 0, executionTimeMs: 0 },
    };
  }

  // If we have a pre-built AI pipeline, send it directly to /api/query/aggregate
  if (queryDefinition.aiGeneratedPipeline?.length) {
    const response = await dataServiceClient.post(
      '/api/query/aggregate',
      {
        groupId: queryDefinition.groupId,
        schemaId: queryDefinition.schemaId,
        pipeline: queryDefinition.aiGeneratedPipeline,
      },
      axiosConfig,
    );
    return parseAggregateExecuteResponse(response.data);
  }

  // Standard path: call execute-template with the high-level query format
  const dataOwnerOrgId = await resolveDataOwnerOrgIdForQuery(queryDefinition, options);
  const templateQuery = {
    schemaId: queryDefinition.schemaId,
    groupId: queryDefinition.groupId,
    measures: queryDefinition.measures,
    ...(queryDefinition.timeRange ? { timeRange: queryDefinition.timeRange } : {}),
    ...(queryDefinition.groupBy ? { groupBy: queryDefinition.groupBy } : {}),
    ...(queryDefinition.filters ? { filters: queryDefinition.filters } : {}),
    ...(queryDefinition.orderBy ? { orderBy: queryDefinition.orderBy } : {}),
    ...(queryDefinition.limit ? { limit: queryDefinition.limit } : {}),
  };

  const response = await dataServiceClient.post(
    '/api/nl/execute-template',
    {
      query: templateQuery,
      variables: {},
      schemaId: queryDefinition.schemaId,
      groupId: queryDefinition.groupId,
      dataOwnerOrgId,
    },
    axiosConfig,
  );

  return parseTemplateExecuteResponse(response.data);
}

/**
 * Execute a composite query definition (multi-source merge) for chart data refresh.
 * Calls POST /api/nl/execute-composite-template on the data-service.
 * The backend executes each source query independently, joins them using the
 * configured join semantics, and returns one flat rowset.
 */
export async function executeCompositeQueryDefinition(
  compositeQueryDefinition: CompositeQueryDefinition,
  options?: CrossOrgOptions
): Promise<ExecuteQueryResponse> {
  let axiosConfig: import('axios').AxiosRequestConfig | undefined;
  try {
    axiosConfig = await getValidatedCrossOrgAxiosConfig(options);
  } catch (error) {
    return {
      success: false,
      data: [],
      error: error instanceof Error ? error.message : INVALID_CROSS_ORG_CONTEXT_MESSAGE,
    };
  }

  const response = await dataServiceClient.post(
    '/api/nl/execute-composite-template',
    { compositeQuery: compositeQueryDefinition },
    {
      ...axiosConfig,
      timeout: 120000,
    },
  );

  return parseTemplateExecuteResponse(response.data);
}

// ============================================================================
// Query Template API
// ============================================================================

export const queryTemplateApi = {
  /**
   * Create a new query template
   */
  async create(
    dto: CreateQueryTemplateDTO,
    options?: Pick<CrossOrgOptions, 'organizationId'>,
  ): Promise<QueryTemplate> {
    validateCompositeTemplateSourceOwners(dto);
    const axiosConfig = await getValidatedCrossOrgAxiosConfig(options);
    const response = await dataServiceClient.post<ApiResponse<QueryTemplate>>(
      '/api/query-templates',
      dto,
      axiosConfig,
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to create template');
    }

    return response.data.data;
  },

  /**
   * Get a template by ID
   */
  async getById(
    templateId: string,
    options?: Pick<CrossOrgOptions, 'organizationId'>,
  ): Promise<QueryTemplate | null> {
    try {
      const axiosConfig = await getValidatedCrossOrgAxiosConfig(options);
      const response = await dataServiceClient.get<ApiResponse<QueryTemplate>>(
        `/api/query-templates/${templateId}`,
        axiosConfig,
      );

      if (!response.data.success) {
        return null;
      }

      return response.data.data || null;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * List templates with optional filters
   */
  async list(
    options: ListTemplatesOptions = {},
    axiosConfig?: import('axios').AxiosRequestConfig
  ): Promise<{
    templates: QueryTemplate[];
    total: number;
  }> {
    const params = new URLSearchParams();

    if (options.tags?.length) params.set('tags', options.tags.join(','));
    if (options.search) params.set('search', options.search);
    if (options.schemaId) params.set('schemaId', options.schemaId);
    if (options.groupId) params.set('groupId', options.groupId);
    if (options.folder !== undefined) params.set('folder', options.folder);
    if (options.sortBy) params.set('sortBy', options.sortBy);
    if (options.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options.limit) params.set('limit', String(options.limit));
    if (options.skip) params.set('skip', String(options.skip));

    const queryString = params.toString();
    const url = `/api/query-templates${queryString ? `?${queryString}` : ''}`;

    const response = await dataServiceClient.get<PaginatedResponse<QueryTemplate>>(
      url,
      axiosConfig?.headers ? { headers: axiosConfig.headers } : undefined
    );

    return {
      templates: response.data.data || [],
      total: response.data.pagination?.total || 0,
    };
  },

  /**
   * Update a template
   */
  async update(
    templateId: string,
    dto: Partial<CreateQueryTemplateDTO>
  ): Promise<QueryTemplate> {
    const response = await dataServiceClient.put<ApiResponse<QueryTemplate>>(
      `/api/query-templates/${templateId}`,
      dto
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to update template');
    }

    return response.data.data;
  },

  /**
   * Delete a template
   */
  async delete(templateId: string): Promise<void> {
    const response = await dataServiceClient.delete<ApiResponse<{ deleted: boolean }>>(
      `/api/query-templates/${templateId}`
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete template');
    }
  },

  /**
   * Execute a template with variable overrides
   */
  async execute(
    templateId: string,
    variables: Record<string, unknown> = {},
    options?: CrossOrgOptions
  ): Promise<TemplateExecutionResult> {
    const axiosConfig = await getValidatedCrossOrgAxiosConfig(options);
    const response = await dataServiceClient.post<ApiResponse<TemplateExecutionResult>>(
      `/api/query-templates/${templateId}/execute`,
      {
        variables,
        ...(options?.skipCache ? { skipCache: true } : {}),
      },
      // Per-component org override for cross-org data access in personal space
      {
        ...(axiosConfig ?? {}),
        ...(options?.skipCache
          ? { params: { skipCache: 'true' } }
          : {}),
      }
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to execute template');
    }

    return response.data.data;
  },

  /**
   * Validate variable overrides before execution
   */
  async validate(
    templateId: string,
    variables: Record<string, unknown> = {}
  ): Promise<{ valid: boolean; errors: string[] }> {
    const response = await dataServiceClient.post<
      ApiResponse<{ valid: boolean; errors: string[] }>
    >(`/api/query-templates/${templateId}/validate`, { variables });

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to validate template');
    }

    return response.data.data;
  },

  /**
   * Get variable definitions for a template
   */
  async getVariables(
    templateId: string
  ): Promise<Record<string, VariableDefinition>> {
    const response = await dataServiceClient.get<
      ApiResponse<Record<string, VariableDefinition>>
    >(`/api/query-templates/${templateId}/variables`);

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to get template variables');
    }

    return response.data.data;
  },
};

// ============================================================================
// Template Folder API
// ============================================================================

export interface TemplateFolderRecord {
  id: string;
  name: string;
  createdAt: string;
}

export const templateFolderApi = {
  async list(axiosConfig?: import('axios').AxiosRequestConfig): Promise<TemplateFolderRecord[]> {
    const response = await dataServiceClient.get<ApiResponse<TemplateFolderRecord[]>>(
      '/api/template-folders',
      axiosConfig?.headers ? { headers: axiosConfig.headers } : undefined
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to list folders');
    }
    return response.data.data;
  },

  async create(name: string): Promise<TemplateFolderRecord> {
    const response = await dataServiceClient.post<ApiResponse<TemplateFolderRecord>>(
      '/api/template-folders',
      { name }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to create folder');
    }
    return response.data.data;
  },

  async rename(id: string, name: string): Promise<TemplateFolderRecord> {
    const response = await dataServiceClient.put<ApiResponse<TemplateFolderRecord>>(
      `/api/template-folders/${id}`,
      { name }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to rename folder');
    }
    return response.data.data;
  },

  async remove(id: string): Promise<void> {
    const response = await dataServiceClient.delete<ApiResponse<{ deleted: boolean }>>(
      `/api/template-folders/${id}`
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete folder');
    }
  },
};

export default queryTemplateApi;
